/**
 * Villager trade screen (self-contained DOM overlay).
 *
 * There was no trade UI anywhere in `ui/` (and that tree is owned by another
 * agent), so this lives with the village code and is shown by main.ts's
 * VillageDeps.openTrade. It lists the villager's offers and, on click, pays
 * from the player's inventory and hands back the result — no payment slots,
 * which keeps it usable without the container-screen machinery.
 *
 * Vanilla-ish: offers lock at maxUses, the result respects enchants, and the
 * villager gains trade XP (leveling on close is handled by Villager.onTradeClosed).
 */

import type { Inventory } from '../../game/inventory';
import { LEVEL_NAMES, outOfStock, priceA, playerTradeXp, useOffer, type Offer } from '../../game/trading';
import type { Villager } from './villager';

/** Anything with offers to trade: a villager, or the wandering trader (which sets `title`). */
export interface TradeTarget { offers: Offer[]; xp: number; title?: string }

export interface TradeDeps {
  inventory: Inventory;
  addXp(points: number): void;
  nameOf(id: number): string;
  /** Item icon canvas (32x32), or null when there is none. */
  iconFor(id: number): HTMLCanvasElement | null;
  playSound(id: string): void;
  /** Server mode: the trade is the server's to make; send it instead of trading locally. */
  remote?(idx: number): void;
}

export class VillagerTradeScreen {
  private readonly root: HTMLDivElement;
  private readonly list: HTMLDivElement;
  private villager: TradeTarget | null = null;
  private onClose: (() => void) | null = null;

  constructor(parent: HTMLElement, private readonly d: TradeDeps) {
    this.root = document.createElement('div');
    this.root.style.cssText = [
      'position:fixed', 'inset:0', 'display:none', 'align-items:center', 'justify-content:center',
      'z-index:60', 'font:13px/1.4 monospace', 'color:#e8e8e8',
      'background:linear-gradient(rgba(16,16,16,0.75),rgba(16,16,16,0.82))', 'user-select:none',
    ].join(';');
    const panel = document.createElement('div');
    panel.style.cssText = [
      'min-width:280px', 'max-width:min(520px,90vw)', 'max-height:80vh', 'overflow:auto',
      'background:#c6c6c6', 'color:#3f3f3f', 'border:2px solid #000',
      'box-shadow:inset 2px 2px 0 #fff, inset -2px -2px 0 #555', 'padding:10px 12px', 'image-rendering:pixelated',
    ].join(';');

    const title = document.createElement('div');
    title.textContent = 'Villager';
    title.style.cssText = 'font-weight:bold;margin-bottom:8px;color:#3f3f3f;';
    this.titleEl = title;
    this.list = document.createElement('div');
    panel.append(title, this.list);

    const hint = document.createElement('div');
    hint.textContent = 'Click a trade  ·  Esc / E to close';
    hint.style.cssText = 'margin-top:8px;font-size:11px;color:#5a5a5a;';
    panel.append(hint);

    this.root.append(panel);
    parent.append(this.root);

    // clicking the dim background (not the panel) closes
    this.root.addEventListener('mousedown', (e) => { if (e.target === this.root) this.close(); });
  }

  private readonly titleEl: HTMLDivElement;

  get open(): boolean { return this.villager !== null; }

  openTrade(v: TradeTarget, onClose: () => void): void {
    this.villager = v;
    this.onClose = onClose;
    const vv = v as Partial<Villager>;
    this.titleEl.textContent = v.title ?? `${vv.profession === undefined || vv.profession === 'none' ? 'Villager' : capital(vv.profession)}  -  ${LEVEL_NAMES[Math.max(0, Math.min(4, (vv.level ?? 1) - 1))]}`;
    this.render();
    this.root.style.display = 'flex';
    try { document.exitPointerLock?.(); } catch { /* not locked */ }
  }

  close(): void {
    if (!this.villager) return;
    this.villager = null;
    this.root.style.display = 'none';
    const cb = this.onClose;
    this.onClose = null;
    cb?.();
  }

  /** Redraw with new offers (server mode: the server sent the list again). */
  refresh(offers: Offer[], xp: number): void {
    if (!this.villager) return;
    this.villager.offers = offers; this.villager.xp = xp;
    this.render();
  }

  /** One trade attempt; returns whether anything happened. */
  private trade(idx: number): boolean {
    const v = this.villager;
    if (!v) return false;
    const o = v.offers[idx];
    if (!o || outOfStock(o)) return false;
    const needA = priceA(o);
    if (this.d.inventory.countOf(o.a.id) < needA) return false;
    if (o.b && this.d.inventory.countOf(o.b.id) < o.b.count) return false;
    const out = o.out;
    const dmg = out.dmg ?? 0;
    if (!this.d.inventory.canAdd(out.id, out.count, dmg, out.ench)) return false;
    if (this.d.remote) { this.d.remote(idx); this.d.playSound('entity.villager.trade'); return true; }
    this.d.inventory.remove(o.a.id, needA);
    if (o.b) this.d.inventory.remove(o.b.id, o.b.count);
    this.d.inventory.add(out.id, out.count, dmg, out.ench);
    v.xp += useOffer(o);
    this.d.addXp(playerTradeXp(Math.random));
    this.d.playSound('entity.villager.trade');
    this.render();
    return true;
  }

  private render(): void {
    const v = this.villager;
    if (!v) return;
    this.list.textContent = '';
    if (v.offers.length === 0) {
      const none = document.createElement('div');
      none.textContent = 'Nothing to trade.';
      this.list.append(none);
      return;
    }
    v.offers.forEach((o, i) => this.list.append(this.row(o, i)));
  }

  private row(o: Offer, idx: number): HTMLDivElement {
    const locked = outOfStock(o);
    const needA = priceA(o);
    const affordA = this.d.inventory.countOf(o.a.id) >= needA;
    const affordB = !o.b || this.d.inventory.countOf(o.b.id) >= o.b.count;
    const canFit = this.d.inventory.canAdd(o.out.id, o.out.count, o.out.dmg ?? 0, o.out.ench);
    const ok = !locked && affordA && affordB && canFit;

    const el = document.createElement('div');
    el.style.cssText = [
      'display:flex', 'align-items:center', 'gap:8px', 'padding:6px 8px', 'margin:4px 0',
      'background:#8b8b8b', 'box-shadow:inset 2px 2px 0 #373737, inset -2px -2px 0 #fff',
      locked ? 'opacity:0.45;filter:grayscale(1);' : '',
      ok ? 'cursor:pointer;' : 'cursor:default;',
    ].join(';');
    el.append(this.slot(o.a.id, needA, affordA));
    if (o.b) el.append(sym('+'), this.slot(o.b.id, o.b.count, affordB));
    el.append(locked ? lockedSym() : sym('→'), this.slot(o.out.id, o.out.count, true));
    const uses = document.createElement('div');
    uses.textContent = locked ? 'sold out' : `${o.maxUses - o.uses} left`;
    uses.style.cssText = 'margin-left:auto;font-size:11px;color:#333;';
    el.append(uses);
    if (ok) el.addEventListener('mousedown', (e) => { e.preventDefault(); this.trade(idx); });
    return el;
  }

  private slot(id: number, count: number, lit: boolean): HTMLDivElement {
    const s = document.createElement('div');
    s.style.cssText = [
      'position:relative', 'width:36px', 'height:36px', 'flex:none',
      'background:#8b8b8b', 'box-shadow:inset 2px 2px 0 #373737, inset -2px -2px 0 #fff',
      'display:flex', 'align-items:center', 'justify-content:center',
      lit ? '' : 'opacity:0.5;',
    ].join(';');
    const icon = this.d.iconFor(id);
    if (icon) {
      // a cloned canvas has no bitmap: draw the source into a fresh one
      const c = document.createElement('canvas');
      c.width = icon.width; c.height = icon.height;
      c.getContext('2d')?.drawImage(icon, 0, 0);
      c.style.cssText = 'width:20px;height:20px;image-rendering:pixelated;';
      s.append(c);
    } else {
      const t = document.createElement('span');
      t.textContent = this.d.nameOf(id).slice(0, 2);
      t.style.cssText = 'font-size:10px;';
      s.append(t);
    }
    if (count > 1) {
      const n = document.createElement('span');
      n.textContent = String(count);
      n.style.cssText = 'position:absolute;right:1px;bottom:-2px;font-size:11px;font-weight:bold;color:#fff;text-shadow:1px 1px 0 #000;';
      s.append(n);
    }
    return s;
  }
}

function lockedSym(): HTMLSpanElement {
  const s = sym('✖');
  s.style.color = '#b02020';
  return s;
}

function sym(t: string): HTMLSpanElement {
  const s = document.createElement('span');
  s.textContent = t;
  s.style.cssText = 'font-weight:bold;color:#3f3f3f;';
  return s;
}

const capital = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);
